import { hash32 } from '@vm/shared';
import type { CamoDef } from '../cosmetics';
import { ACC, ACC_D, ACC_L, CAMO_BASE, LIGHT, PRI, PRI_D, SEC, SEC_D, SEC_L } from './builder';

/** Scales a color: f > 1 lifts toward white, f < 1 darkens. */
export function tone(rgb: number, f: number): number {
  let r = (rgb >> 16) & 255;
  let g = (rgb >> 8) & 255;
  let b = rgb & 255;
  if (f >= 1) {
    const k = (f - 1) * 0.9;
    r += (255 - r) * k;
    g += (255 - g) * k;
    b += (255 - b) * k;
  } else {
    r *= f;
    g *= f;
    b *= f;
  }
  return (Math.min(255, Math.round(r)) << 16) | (Math.min(255, Math.round(g)) << 8) | Math.min(255, Math.round(b));
}

export function mix(a: number, b: number, t: number): number {
  const r = ((a >> 16) & 255) + ((((b >> 16) & 255) - ((a >> 16) & 255)) * t);
  const g = ((a >> 8) & 255) + ((((b >> 8) & 255) - ((a >> 8) & 255)) * t);
  const bl = (a & 255) + (((b & 255) - (a & 255)) * t);
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(bl);
}

/** Camo-affected slots: slot -> [role (0 primary, 1 secondary, 2 accent), shade]. */
const ROLE: Record<number, [number, number]> = {
  [PRI]: [0, 1],
  [LIGHT]: [0, 1.24],
  [PRI_D]: [0, 0.72],
  [SEC]: [1, 1],
  [SEC_L]: [1, 1.22],
  [SEC_D]: [1, 0.72],
  [ACC]: [2, 1],
  [ACC_L]: [2, 1.22],
  [ACC_D]: [2, 0.72],
};

interface CamoResult {
  grid: Uint8Array;
  palette: number[];
  emissive: number[];
}

/** True when the camo actually changes anything (the factory finish does not). */
export function camoActive(c: CamoDef | null | undefined): c is CamoDef {
  return !!c && (c.colors[0] !== 0 || c.colors[1] !== 0 || c.colors[2] !== 0);
}

/**
 * Recolors primary/secondary/accent voxels with a camo pattern computed per voxel.
 * Only surface voxels are recolored (interior voxels never produce faces).
 */
export function applyCamo(grid: Uint8Array, sx: number, sy: number, sz: number, basePalette: number[], baseEmissive: number[], camo: CamoDef, seed: number): CamoResult {
  const out = grid.slice();
  const palette = basePalette.slice();
  while (palette.length < CAMO_BASE) palette.push(0);
  const emissive = baseEmissive.slice();
  const interned = new Map<number, number>();
  const intern = (rgb: number, glow: boolean): number => {
    const key = rgb * 2 + (glow ? 1 : 0);
    let i = interned.get(key);
    if (i === undefined) {
      if (palette.length >= 254) return glow ? baseEmissive[0] : PRI;
      i = palette.length;
      palette.push(rgb);
      if (glow) emissive.push(i);
      interned.set(key, i);
    }
    return i;
  };
  const at = (x: number, y: number, z: number): number => (x < 0 || y < 0 || z < 0 || x >= sx || y >= sy || z >= sz ? 0 : grid[x + z * sx + y * sx * sz]);
  const [c0, c1, c2] = camo.colors;
  const glowColor = camo.glow ?? c2;
  for (let y = 0; y < sy; y++)
    for (let z = 0; z < sz; z++)
      for (let x = 0; x < sx; x++) {
        const i = x + z * sx + y * sx * sz;
        const role = ROLE[grid[i]];
        if (!role) continue;
        const top = at(x, y + 1, z) === 0;
        const surface = top || at(x - 1, y, z) === 0 || at(x + 1, y, z) === 0 || at(x, y - 1, z) === 0 || at(x, y, z - 1) === 0 || at(x, y, z + 1) === 0;
        if (!surface) continue;
        const [r, shade] = role;
        const secondary = r === 1 ? 0.86 : 1;
        let rgb = r === 0 ? c0 : r === 1 ? c1 : c2;
        let glow = false;
        const h = hash32(x, y, z, seed);
        switch (camo.pattern) {
          case 'solid':
            break;
          case 'stripes': {
            if (r === 2) break;
            const wob = (hash32(y >> 1, z >> 2, seed) & 3) - 1.5;
            const band = Math.floor((z * 0.9 + y * 1.5 + wob) / 3);
            rgb = tone(((band % 3) + 3) % 3 === 0 ? c1 : c0, secondary);
            break;
          }
          case 'digital': {
            const cell = hash32(x >> 1, y >> 1, z >> 1, seed) % 100;
            const fine = hash32(x, y, z, seed + 7) % 100;
            const pick = cell < 46 ? c0 : cell < 82 ? c1 : c2;
            rgb = r === 2 ? c2 : tone(fine < 12 ? (pick === c0 ? c1 : c0) : pick, secondary);
            break;
          }
          case 'splinter': {
            if (r === 2) break;
            const a = Math.floor((z + y * 2.2 + x * 0.4) / 6);
            const bnd = Math.floor((z * 0.55 - y * 1.8 + 40) / 5);
            const k = hash32(a, bnd, seed) % 7;
            rgb = tone(k < 3 ? c0 : k < 6 ? c1 : c2, secondary);
            break;
          }
          case 'gradient': {
            if (r === 2) break;
            const t = Math.min(1, Math.max(0, (z / Math.max(1, sz - 1)) * 0.75 + (1 - y / Math.max(1, sy - 1)) * 0.35 - 0.05));
            const step = Math.round(t * 5) / 5;
            rgb = mix(c0, c1, step);
            break;
          }
          case 'metallic': {
            const base = r === 1 ? tone(c0, 0.82) : r === 2 ? c1 : c0;
            const diag = (z + y * 2 + x) % 14;
            const tri = diag < 7 ? diag / 7 : (14 - diag) / 7;
            const level = Math.round(tri * 3) / 3;
            rgb = tone(base, 0.84 + level * 0.36);
            if (top) rgb = mix(rgb, c2, 0.45);
            if (camo.glow && h % 31 === 0) {
              rgb = camo.glow;
              glow = true;
            }
            break;
          }
          case 'glow': {
            const base = r === 1 ? tone(c0, 0.8) : c0;
            rgb = tone(base, secondary);
            const lane = (y + (hash32(z >> 3, seed) & 1)) % 3 === 1 && hash32(z >> 2, y, seed) % 4 !== 0;
            const rung = z % 6 === 0 && hash32(y >> 1, z, seed) % 3 === 0;
            if (r === 2 || lane || rung) {
              rgb = glowColor;
              glow = true;
            } else if (h % 9 === 0) rgb = c1;
            break;
          }
        }
        if (!glow) rgb = tone(rgb, shade);
        out[i] = intern(rgb, glow);
      }
  return { grid: out, palette, emissive };
}

/** Palette slots whose colors a camo replaces (exported for tests). */
export const CAMO_SLOTS = Object.keys(ROLE).map(Number);
